import { useDrawer } from '@/components/common/DrawerManager';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import { ArrowRight, Clock, Shield01 } from '@/components/common/icons';
import { showCompleteSetupOnMobileModal } from '@/components/goals/helpers';
import { toDollarsWithCents } from '@/util/currency';
import { BoxProps } from '@chakra-ui/react';
import { isPlentyGoalAccount } from '@common/financialEntities';
import { Goal } from '@common/goals';
import {
    getGoalAccountOwnershipDetails,
    getGoalBalanceForPageMode,
    isGoalSetupIncomplete,
    useGoalState,
    useGoalSubtitle,
} from '@common/react/goals';
import { usePageContext } from '@common/react/pageMode';
import { useCurrentRateInfo } from '@common/react/rates';
import { isNull, isNumber } from '@common/util/types';
import { FC } from 'react';
import { ArrowLinkText } from '../common/ArrowLinkText';
import { useBalanceChangeDateRangeState } from '../common/BalanceChange';
import HistoricalBalanceChange from '../common/HistoricalBalanceChange';
import IconBackground from '../common/icons/IconBackground';
import PartnerAvatar from '../common/PartnerAvatar';
import { hoverStyleProps } from '../common/styleHelpers';
import { Text } from '../common/Typography';
import CarouselPill from '../dashboard/CarouselPill';
import GoalIcon from './GoalIcon';
import { useAccountSetupSteps } from './useAccountSetupSteps';

interface GoalItemProps extends BoxProps {
    goal: Goal;
    variant: 'pill' | 'tile';
}

const GoalItem: FC<GoalItemProps> = ({ goal, variant, ...props }) => {
    const { pageMode } = usePageContext();
    const { currentRateInfo } = useCurrentRateInfo();
    const { showDrawer } = useDrawer();
    const { handleNextStep } = useAccountSetupSteps({
        checkJointAccountConsent: true,
    });
    const [balanceChangeDateRange] = useBalanceChangeDateRangeState();
    const details = getGoalAccountOwnershipDetails(goal);
    const goalState = useGoalState(goal);
    const subtitle = useGoalSubtitle({ goal, platform: 'web' });

    if (!currentRateInfo) return null;

    const { balance, suffixBalance } = details
        ? getGoalBalanceForPageMode({
              details,
              pageMode,
          })
        : { balance: null, suffixBalance: undefined };

    // TODO: Reconsider this assumption; potentially extract into canonical helper.
    const isUnstarted = isNull(balance);

    const isReadonly =
        goalState === 'PENDING_JOINT_APPROVAL' ||
        goalState === 'PARTNER_SETTING_UP' ||
        goalState === 'WAITING_FOR_PARTNER_JOINT_CONSENT';

    const handleClick = () => {
        if (isReadonly) return;

        if (goalState === 'WAITING_FOR_JOINT_CONSENT') {
            handleNextStep({});
            return;
        }

        if (isGoalSetupIncomplete(goal) && goal.onboardingVersion === 'v2') {
            showCompleteSetupOnMobileModal();
            return;
        }

        showDrawer({
            drawerKey: 'goal-detail',
            entityId: goal.id,
        });
    };

    if (variant === 'pill') {
        const left =
            goalState === 'PENDING_JOINT_APPROVAL' ? (
                <Shield01 size="xs" />
            ) : goalState === 'PARTNER_SETTING_UP' ||
              goalState === 'WAITING_FOR_PARTNER_JOINT_CONSENT' ? (
                <PartnerAvatar size="xs" />
            ) : isUnstarted ? (
                <ArrowRight size="sm" color="blue.500" />
            ) : (
                <HistoricalBalanceChange
                    dateRange={balanceChangeDateRange}
                    positiveAccounts={goal.financialEntities.filter(
                        isPlentyGoalAccount
                    )}
                    noText
                />
            );

        return (
            <CarouselPill
                isReadOnly={isReadonly}
                onClick={handleClick}
                justify="space-between"
                align="center"
                tooltip={
                    goalState === 'NO_INVESTMENTS' ||
                    goalState === 'HAS_INVESTMENTS'
                        ? goal.name
                        : subtitle
                }
                {...props}
            >
                <FlexRow align="center" gap={2}>
                    <GoalIcon showProgress={true} goal={goal} />
                    <FlexCol align="flex-start" ml={1}>
                        <Text
                            fontSize="sm"
                            fontWeight="medium"
                            color={isUnstarted ? 'gray.400' : 'gray.700'}
                        >
                            {toDollarsWithCents(balance ?? 0)}
                        </Text>
                        {isNumber(suffixBalance) && (
                            <Text fontSize="xs" color="gray.500">
                                of {toDollarsWithCents(suffixBalance ?? 0)}
                            </Text>
                        )}
                    </FlexCol>
                </FlexRow>
                {left}
            </CarouselPill>
        );
    } else {
        return (
            <FlexCol
                h={60}
                border="1px solid"
                borderColor="border"
                rounded="md"
                onClick={handleClick}
                {...(isReadonly
                    ? {}
                    : hoverStyleProps({
                          shadow: 'md',
                          transform: 'translateY(-3%)',
                      }))}
                py={6}
                pl={5}
                pr={8}
                role="group"
                {...props}
            >
                <GoalIcon showProgress={true} size="xl" goal={goal} />
                <Text mt={5} mb={2} fontWeight="semibold">
                    {goal.name}
                </Text>
                <Text fontFamily="mono" color="textMuted" fontSize="sm">
                    {subtitle}
                </Text>
                <FlexRow grow={1} justify="space-between" align="end">
                    {isReadonly ? (
                        <FlexRow alignItems="center">
                            {goalState === 'PENDING_JOINT_APPROVAL' ? (
                                <IconBackground shape="circle" bg="gray.100">
                                    <Clock size="sm" />
                                </IconBackground>
                            ) : (
                                <PartnerAvatar />
                            )}
                            <Text fontSize="sm" color="textMuted" ml={6}>
                                {subtitle}
                            </Text>
                        </FlexRow>
                    ) : (
                        <>
                            <FlexCol align="flex-start" ml={1}>
                                <Text
                                    fontSize="sm"
                                    fontWeight="semibold"
                                    color={
                                        isUnstarted ? 'gray.400' : 'gray.700'
                                    }
                                >
                                    {toDollarsWithCents(balance ?? 0)}
                                </Text>
                                {isNumber(suffixBalance) && (
                                    <Text fontSize="xs" color="gray.500">
                                        of{' '}
                                        {toDollarsWithCents(suffixBalance ?? 0)}
                                    </Text>
                                )}
                            </FlexCol>
                            {isUnstarted ? (
                                <ArrowLinkText
                                    fontWeight="semibold"
                                    id={activeGoalTileStartId(goal.id)}
                                >
                                    Start
                                </ArrowLinkText>
                            ) : (
                                <HistoricalBalanceChange
                                    dateRange={balanceChangeDateRange}
                                    positiveAccounts={goal.financialEntities.filter(
                                        isPlentyGoalAccount
                                    )}
                                    noDescription
                                    noChangeText=""
                                />
                            )}
                        </>
                    )}
                </FlexRow>
            </FlexCol>
        );
    }
};

export const activeGoalTileStartId = (id: string) =>
    `active-goal-tile-start-${id}`;

export default GoalItem;
